import { IconCalendar, IconChevronDown } from "@tabler/icons-react";
import classNames from "classnames";
import moment, { type Moment } from "moment-jalaali";
import {
  type ButtonHTMLAttributes,
  type FocusEventHandler,
  type Ref,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import gregorian from "react-date-object/calendars/gregorian";
import persian from "react-date-object/calendars/persian";
import gregorian_en from "react-date-object/locales/gregorian_en";
import persian_fa from "react-date-object/locales/persian_fa";
import DatePickerRootModule, {
  Calendar as CalendarModule,
  DateObject as RawDateObject,
} from "react-multi-date-picker";
import MultiDateTimePickerPlugin from "react-multi-date-picker/plugins/time_picker";
import { __ } from "@common/helpers/i18n";
import {
  DatePickerProps,
  type DatePickerRangeValue,
  type DatePickerValue,
} from "@common/types";
import { useDirection } from "@app/hooks";
import "./datepicker.css";
import { Modal } from "../modals/Modal";
import { Button } from "./Button";
import { getInputClassName } from "./Input";

type DatePickerNativeProps = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  "value" | "defaultValue" | "onChange" | "min" | "max"
>;

type AppDatePickerProps = DatePickerProps &
  DatePickerNativeProps & {
    className?: string;
    defaultMonth?: DatePickerValue;
    min?: DatePickerValue;
    max?: DatePickerValue;
  };

const dateFormat = "YYYY/MM/DD";
const dateTimeFormat = "YYYY/MM/DD HH:mm";
const MultiDateTimePicker =
  (
    MultiDateTimePickerPlugin as {
      default?: typeof MultiDateTimePickerPlugin;
    }
  ).default || MultiDateTimePickerPlugin;
const Calendar =
  CalendarModule ||
  (
    DatePickerRootModule as {
      Calendar?: typeof CalendarModule;
    }
  ).Calendar;
const DateObject =
  RawDateObject ||
  (
    DatePickerRootModule as {
      DateObject?: typeof RawDateObject;
    }
  ).DateObject;

function getPickerFormat({
  isRtl,
  timePicker,
  inputFormat,
  inputJalaaliFormat,
}: {
  isRtl: boolean;
  timePicker?: boolean;
  inputFormat?: string;
  inputJalaaliFormat?: string;
}) {
  const fallbackFormat = timePicker ? dateTimeFormat : dateFormat;

  if (isRtl) {
    return inputJalaaliFormat || fallbackFormat;
  }

  return inputFormat || fallbackFormat;
}

function normalizeMoment(date: DatePickerValue) {
  if (date === null || date === undefined || date === "") {
    return undefined;
  }

  if (moment.isMoment(date)) {
    return date.isValid() ? date : undefined;
  }

  const parsedDate = moment(date);

  return parsedDate.isValid() ? parsedDate : undefined;
}

function toDateObject(date: DatePickerValue, isRtl: boolean, format: string) {
  const normalizedDate = normalizeMoment(date);

  if (!normalizedDate) {
    return undefined;
  }

  return new DateObject({
    date: normalizedDate.toDate(),
    calendar: isRtl ? persian : gregorian,
    locale: isRtl ? persian_fa : gregorian_en,
    format,
  });
}

function toDateObjectRange(
  value: DatePickerRangeValue,
  isRtl: boolean,
  format: string,
) {
  if (!Array.isArray(value)) {
    return undefined;
  }

  const range = value
    .slice(0, 2)
    .map((date) => toDateObject(date, isRtl, format))
    .filter((date): date is RawDateObject => Boolean(date));

  return range.length ? range : undefined;
}

function toMoment(date: RawDateObject) {
  return moment(date.toDate()).second(0).millisecond(0);
}

function isSingleDateObject(date: unknown): date is RawDateObject {
  return date instanceof DateObject;
}

function isDateObjectArray(date: unknown): date is RawDateObject[] {
  return Array.isArray(date) && date.every(isSingleDateObject);
}

function formatRangeValue(
  value: RawDateObject[] | undefined,
  format: string,
  fallback: string,
) {
  if (!value?.length) {
    return fallback;
  }

  return value
    .slice(0, 2)
    .map((date) => date.format(format))
    .join(" - ");
}

export const DatePicker = forwardRef<HTMLButtonElement, AppDatePickerProps>(
  function DatePicker(
    {
      value,
      onChange,
      range,
      placeholder,
      className,
      autoWidth,
      timePicker,
      inputFormat,
      inputJalaaliFormat,
      placement: _placement = "bottom",
      defaultMonth,
      min,
      max,
      showIcon = true,
      showChevron = false,
      clearLabel,
      onClear,
      disabled,
      id,
      name,
      onBlur,
      ...props
    },
    forwardedRef: Ref<HTMLButtonElement>,
  ) {
    const { direction, isRtl } = useDirection();
    const triggerRef = useRef<HTMLButtonElement | null>(null);
    const [isPickerOpen, setIsPickerOpen] = useState(false);
    const [draftValue, setDraftValue] = useState<
      RawDateObject | RawDateObject[] | undefined
    >();
    const isRange = Boolean(range);
    const format = getPickerFormat({
      isRtl,
      timePicker,
      inputFormat,
      inputJalaaliFormat,
    });
    void _placement;

    useImperativeHandle(
      forwardedRef,
      () => triggerRef.current as HTMLButtonElement,
    );

    const pickerValue = useMemo(
      () =>
        isRange
          ? toDateObjectRange(value as DatePickerRangeValue, isRtl, format)
          : toDateObject(value as DatePickerValue, isRtl, format),
      [format, isRange, isRtl, value],
    );

    const currentDate = useMemo(
      () => toDateObject(defaultMonth, isRtl, format),
      [defaultMonth, format, isRtl],
    );

    const minDate = useMemo(
      () => toDateObject(min, isRtl, format),
      [format, isRtl, min],
    );

    const maxDate = useMemo(
      () => toDateObject(max, isRtl, format),
      [format, isRtl, max],
    );

    const makeDraftPickerValue = useCallback(() => {
      if (isRange) {
        return toDateObjectRange(value as DatePickerRangeValue, isRtl, format);
      }

      return toDateObject(
        (value as DatePickerValue) ??
          defaultMonth ??
          (timePicker ? moment() : undefined),
        isRtl,
        format,
      );
    }, [defaultMonth, format, isRange, isRtl, timePicker, value]);

    useEffect(() => {
      if (!isPickerOpen) {
        return;
      }

      setDraftValue(makeDraftPickerValue());
    }, [isPickerOpen, makeDraftPickerValue]);

    const handleBlur: FocusEventHandler<HTMLButtonElement> = (event) => {
      onBlur?.(event);
    };

    const handleDraftChange = (nextDate: unknown) => {
      if (isRange) {
        if (isDateObjectArray(nextDate)) {
          setDraftValue(nextDate.slice(0, 2));
        }

        return;
      }

      if (isSingleDateObject(nextDate)) {
        setDraftValue(nextDate);
      }
    };

    const openPicker = () => {
      setDraftValue(makeDraftPickerValue());
      setIsPickerOpen(true);
    };

    const closePicker = () => {
      setIsPickerOpen(false);
      triggerRef.current?.focus();
    };

    const canConfirm = isRange
      ? isDateObjectArray(draftValue) && draftValue.length === 2
      : isSingleDateObject(draftValue);
    const hasPickerValue = isRange
      ? Boolean((pickerValue as RawDateObject[] | undefined)?.length)
      : Boolean(pickerValue);

    const confirmPicker = () => {
      if (isRange) {
        const [startDate, endDate] = isDateObjectArray(draftValue)
          ? draftValue
          : [];

        (onChange as (date: [Moment | undefined, Moment | undefined]) => void)([
          startDate ? toMoment(startDate) : undefined,
          endDate ? toMoment(endDate) : undefined,
        ]);
        closePicker();

        return;
      }

      if (isSingleDateObject(draftValue)) {
        (onChange as (date: Moment) => void)(toMoment(draftValue));
      }

      closePicker();
    };

    const clearPicker = () => {
      setDraftValue(undefined);
      onClear?.();
      closePicker();
    };

    const displayValue = isRange
      ? formatRangeValue(
          pickerValue as RawDateObject[] | undefined,
          format,
          placeholder || __("Date range"),
        )
      : (pickerValue as RawDateObject | undefined)?.format(format) ||
        placeholder ||
        __("Date");
    const title =
      props["aria-label"] ||
      placeholder ||
      (isRange
        ? __("Date range")
        : timePicker
          ? __("Schedule date and time")
          : __("Date"));
    const description = isRange
      ? __("Choose the date range.")
      : timePicker
        ? __("Choose when this content should be published.")
        : __("Choose the date.");

    return (
      <div
        dir={direction}
        className={classNames("app-date-picker", !autoWidth && "w-full")}
      >
        <button
          {...props}
          ref={triggerRef}
          type="button"
          id={id}
          name={name}
          disabled={disabled}
          className={classNames(
            "app-date-picker__field",
            !autoWidth && "block w-full",
            getInputClassName("default"),
            timePicker && "app-date-picker__field--with-time",
            className,
          )}
          onClick={(event) => {
            props.onClick?.(event);

            if (!event.defaultPrevented) {
              openPicker();
            }
          }}
          onBlur={handleBlur}
          aria-label={props["aria-label"] || placeholder || __("Date")}
          aria-haspopup="dialog"
          aria-expanded={isPickerOpen}
        >
          <span className="app-date-picker__value">
            {showIcon && (
              <IconCalendar
                size={18}
                stroke={2}
                className="app-date-picker__icon"
              />
            )}
            <span className="truncate">{displayValue}</span>
          </span>
          {showChevron && (
            <IconChevronDown
              className="shrink-0 text-muted-foreground"
              size={18}
              stroke={2}
              aria-hidden="true"
            />
          )}
        </button>

        <Modal
          isOpen={isPickerOpen}
          onClose={closePicker}
          showCloseIcon
          size="small"
          padding="none"
          panelClassName="app-date-picker__modal-panel"
        >
          <Modal.Header>
            <div className="flex min-w-0 items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                <IconCalendar size={20} stroke={2} />
              </span>
              <div className="min-w-0">
                <Modal.Title className="text-lg font-extrabold leading-7">
                  {title}
                </Modal.Title>
                <Modal.Description className="mt-1 text-sm font-medium leading-6 text-muted-foreground">
                  {description}
                </Modal.Description>
              </div>
            </div>
          </Modal.Header>

          <Modal.Body className="app-date-picker__modal-body">
            <div className="app-date-picker__modal-calendar">
              <Calendar
                {...({ role: "group" } as Record<string, string>)}
                value={draftValue}
                onChange={handleDraftChange}
                range={isRange}
                calendar={isRtl ? persian : gregorian}
                locale={isRtl ? persian_fa : gregorian_en}
                format={format}
                currentDate={currentDate}
                minDate={minDate}
                maxDate={maxDate}
                shadow={false}
                className={classNames(
                  "app-date-picker__calendar app-date-picker__calendar--modal",
                  isRtl && "app-date-picker__calendar--rtl",
                )}
                plugins={
                  timePicker
                    ? [
                        <MultiDateTimePicker
                          key="time"
                          hideSeconds
                          mStep={5}
                          position="bottom"
                          header
                        />,
                      ]
                    : undefined
                }
              />
            </div>
          </Modal.Body>

          <Modal.Footer className="app-date-picker__modal-footer flex justify-end gap-2">
            {onClear && hasPickerValue && (
              <Button
                type="button"
                variant="outline-light"
                className="h-10 justify-center px-5"
                onClick={clearPicker}
              >
                {clearLabel || __("Clear filter")}
              </Button>
            )}
            <Button
              type="button"
              variant="primary"
              className="h-10 justify-center px-5"
              onClick={confirmPicker}
              disabled={!canConfirm}
            >
              {__("Confirm")}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    );
  },
);
